/**
 * Plant-local calendar helpers. All stored timestamps are UTC epoch ms; the plant's
 * IANA timezone decides what "today" and "the current shift" mean.
 */

export interface ShiftDef {
  id: string;
  code: string;
  /** Minutes after local midnight. A shift with startMin > endMin crosses midnight. */
  startMin: number;
  endMin: number;
}

export interface LocalTime {
  /** YYYY-MM-DD in plant time. */
  date: string;
  /** Minutes after local midnight. */
  minutes: number;
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    fmtCache.set(tz, f);
  }
  return f;
}

export function localTime(nowMs: number, tz: string): LocalTime {
  const parts = Object.fromEntries(
    formatter(tz)
      .formatToParts(new Date(nowMs))
      .map((p) => [p.type, p.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

export function isValidDate(date: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  return addDays(date, 0) === date;
}

function inShift(s: ShiftDef, minutes: number): boolean {
  if (s.startMin === s.endMin) return true;
  if (s.startMin < s.endMin) return minutes >= s.startMin && minutes < s.endMin;
  return minutes >= s.startMin || minutes < s.endMin;
}

/**
 * The shift running now and the production date it belongs to. A shift that crosses
 * midnight (e.g. C 22:00–06:00) belongs to the date on which it started.
 */
export function currentShift<S extends ShiftDef>(
  shifts: readonly S[],
  nowMs: number,
  tz: string,
): { shift: S; date: string } | null {
  if (!shifts.length) return null;
  const { date, minutes } = localTime(nowMs, tz);
  const s = shifts.find((x) => inShift(x, minutes)) ?? shifts[0]!;
  const crossed = s.startMin > s.endMin && minutes < s.endMin;
  return { shift: s, date: crossed ? addDays(date, -1) : date };
}

/** Minute ranges within one day covered by a shift ([start, end), split at midnight). */
function spans(s: ShiftDef): Array<[number, number]> {
  if (s.startMin === s.endMin) return [[0, 1440]];
  if (s.startMin < s.endMin) return [[s.startMin, s.endMin]];
  return [
    [s.startMin, 1440],
    [0, s.endMin],
  ];
}

/**
 * The first pair of shifts whose hours overlap, or null. Overlapping shifts make "the
 * current shift" ambiguous, so the plan people see could differ from the one planned.
 */
export function findShiftOverlap<S extends ShiftDef>(shifts: readonly S[]): [S, S] | null {
  for (let i = 0; i < shifts.length; i++) {
    for (let j = i + 1; j < shifts.length; j++) {
      const a = shifts[i]!;
      const b = shifts[j]!;
      if (spans(a).some(([s1, e1]) => spans(b).some(([s2, e2]) => s1 < e2 && s2 < e1))) return [a, b];
    }
  }
  return null;
}

export const minutesToHHMM = (m: number): string =>
  `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

export function hhmmToMinutes(s: string): number | null {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** "2h 05m" / "14m" style age. */
export function formatAge(ms: number): string {
  const mins = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}
